import React, { useState } from "react";
import { Trash2, RefreshCw } from "lucide-react";
import * as api from "../../api";

/* -----------------------------------------------------------------------------
 * ResetTestDataCard — pre-launch admin utility to wipe orders, chats, uploads
 * and user credit balances. Requires typing "RESET AG7" verbatim to arm.
 * --------------------------------------------------------------------------- */
export function ResetTestDataCard() {
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const CONFIRM_KEY = "RESET AG7";
  const armed = confirmText.trim() === CONFIRM_KEY;

  const run = async () => {
    if (!armed) return;
    if (!window.confirm(
      "ATENCIÓN — Esto eliminará TODOS los pedidos, chats y comprobantes.\n" +
      "Tambien reseteará los saldos a favor de TODOS los usuarios a 0.\n\n" +
      "Los juegos, banners, métodos de pago y cuentas NO se eliminan.\n\n" +
      "¿Continuar?"
    )) return;
    setBusy(true);
    setResult(null);
    try {
      const res = await api.adminResetTestData(CONFIRM_KEY);
      setResult(res);
      setConfirmText("");
    } catch (e) {
      setResult({ ok: false, error: e.message || String(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-3xl bg-ag7-surface border border-red-500/40 p-8 space-y-5"
      data-testid="reset-test-data-card"
    >
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-full bg-red-950/60 border border-red-500 flex items-center justify-center flex-shrink-0">
          <Trash2 className="w-5 h-5 text-red-400" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-xl font-heading font-extrabold tracking-tight text-white">Reset pre-lanzamiento</h3>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-950/60 border border-red-500 text-red-300 uppercase tracking-widest">
              Peligro
            </span>
          </div>
          <p className="text-[11px] text-zinc-400 mt-1">
            Limpia todos los datos de prueba y reinicia el contador de tickets antes de salir en producción.
          </p>
        </div>
      </div>

      <div className="rounded-xl bg-ag7-bg border border-[var(--ag7-border-soft)] p-4 text-[11px] text-zinc-400 space-y-1">
        <p><strong className="text-red-300">Se elimina:</strong> pedidos, mensajes de chat, comprobantes subidos y códigos OTP.</p>
        <p><strong className="text-orange-300">Se resetea:</strong> saldo a favor de todos los usuarios &rarr; 0 Bs; contador de tickets &rarr; próximo pedido será <strong className="text-white">#1</strong>.</p>
        <p><strong className="text-green-300">Se mantiene intacto:</strong> juegos, paquetes, banners, métodos de pago y cuentas (incluyendo administrador).</p>
      </div>

      <div>
        <label className="block text-xs font-mono text-zinc-400 mb-1">
          Escribe <strong className="text-red-300">RESET AG7</strong> para armar:
        </label>
        <input
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="RESET AG7"
          data-testid="reset-confirm-input"
          disabled={busy}
          className="w-full bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-xl px-4 py-2.5 text-white text-xs font-mono focus:outline-none focus:border-red-500 disabled:opacity-50"
        />
      </div>

      <button
        onClick={run}
        disabled={!armed || busy}
        data-testid="reset-test-data-btn"
        className={`w-full font-bold py-3 rounded-xl text-xs flex items-center justify-center gap-2 transition-all ${
          armed && !busy
            ? "bg-red-600 hover:bg-red-500 text-white shadow-lg shadow-red-500/30"
            : "bg-zinc-800 text-zinc-500 cursor-not-allowed"
        }`}
      >
        {busy ? (
          <><RefreshCw className="w-4 h-4 animate-spin" /> Limpiando...</>
        ) : (
          <><Trash2 className="w-4 h-4" /> Ejecutar Reset (irreversible)</>
        )}
      </button>

      {result && (
        <div
          data-testid="reset-result"
          className={`rounded-xl p-3 text-[11px] font-mono ${
            result.ok
              ? "bg-green-950/40 border border-green-500/40 text-green-200"
              : "bg-red-950/40 border border-red-500/40 text-red-200"
          }`}
        >
          {result.ok ? (
            <>
              <p className="font-bold mb-1">Reset completado</p>
              <ul className="space-y-0.5">
                <li>Pedidos eliminados: <strong className="text-white">{result.deleted?.orders ?? 0}</strong></li>
                <li>Chats eliminados: <strong className="text-white">{result.deleted?.order_messages ?? 0}</strong></li>
                <li>Comprobantes eliminados: <strong className="text-white">{result.deleted?.uploaded_files ?? 0}</strong></li>
                <li>Códigos OTP eliminados: <strong className="text-white">{result.deleted?.password_reset_otps ?? 0}</strong></li>
                <li>Saldos reseteados: <strong className="text-white">{result.users_credits_reset ?? 0}</strong> usuarios</li>
                <li>Próximo pedido será <strong className="text-white">#{(result.order_counter ?? 0) + 1}</strong></li>
              </ul>
            </>
          ) : (
            <>
              <p className="font-bold mb-1">Error</p>
              <p>{result.error}</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
